import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Home, TrendingUp, ShieldCheck, MapPin, Check, Loader2, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/landing/Footer";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const SITE = "https://lagooncityrealestate.ca";

const PERKS = [
  { icon: TrendingUp, title: "Based on real sales", text: "Your estimate is grounded in recent Lagoon City and waterfront comparables — not a generic algorithm." },
  { icon: ShieldCheck, title: "No cost, no obligation", text: "A free, private evaluation from a full-time Lagoon City resident and broker. No pressure to list." },
  { icon: Home, title: "Local canal-market expertise", text: "Canal frontage, boat docks and lot position all affect value — Joselle knows exactly how they price." },
];

const PROPERTY_TYPES = ["Detached home", "Canal / waterfront home", "Condo / townhouse", "Cottage", "Vacant land", "Other"];
const COUNTS = ["1", "2", "3", "4", "5+"];
const TIMEFRAMES = ["Just curious", "Within 3 months", "3–6 months", "6–12 months", "Not sure yet"];

export default function HomeEvaluation() {
  const [form, setForm] = useState({
    address: "",
    property_type: "",
    bedrooms: "",
    bathrooms: "",
    timeframe: "",
    name: "",
    email: "",
    phone: "",
  });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.address.trim()) return;
    setStatus("sending");
    try {
      await api.post("/home-evaluation", form);
      trackEvent("generate_lead", { method: "home_evaluation" });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  const canonical = `${SITE}/home-evaluation`;
  const title = "Free Home Evaluation — What's Your Lagoon City Home Worth? | Joselle Alves";
  const desc =
    "Get a free, no-obligation home evaluation for your Lagoon City or waterfront property. Enter your address and Joselle Alves will send you what your home could sell for in today's market.";

  const fieldCls =
    "w-full rounded-xl border border-[#E890A7]/25 bg-[#0D0B0E]/60 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none focus:border-[#E890A7]/60 transition-colors";

  return (
    <div className="bg-[#0D0B0E] min-h-screen text-slate-100">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="Lagoon City home evaluation, what is my home worth, free home valuation Lagoon City, sell my waterfront home, Lagoon City home value, Ontario home evaluation"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE}/assets/community/lagoon-city-main.webp`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <PageHeader />

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="/assets/community/lagoon-city-main.webp"
            alt="Aerial view of Lagoon City waterfront homes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/85 to-[#0D0B0E]/60" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16 sm:pt-28 sm:pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#F2A7BA] mb-4 flex items-center gap-2">
              <Home size={14} /> Free Home Evaluation
            </p>
            <h1
              data-testid="eval-title"
              className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight"
            >
              What&#39;s your <em className="italic text-[#E890A7]">home worth?</em>
            </h1>
            <p className="mt-6 max-w-xl text-slate-300 font-light text-lg">
              Get a <strong className="text-slate-100 font-medium">Free, No-Obligation</strong> home evaluation for your
              property, prepared personally by Joselle, using real, recent sales data and her nearly 30 years of real
              estate experience. No cost, no pressure — just expert insight into your home&#39;s value.
            </p>
          </motion.div>

          {/* Form card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-[2rem] border border-[#E890A7]/25 bg-[#151218]/90 backdrop-blur-xl p-8 sm:p-10"
          >
            {status === "done" ? (
              <div className="text-center py-8" data-testid="eval-success">
                <span className="grid place-items-center h-16 w-16 rounded-full bg-[#E890A7]/15 border border-[#E890A7]/30 mx-auto mb-6">
                  <Check size={30} className="text-[#E890A7]" />
                </span>
                <h2 className="font-display text-3xl">Request received</h2>
                <p className="mt-3 text-slate-400 font-light">
                  Joselle will personally review recent sales near{" "}
                  <span className="text-[#F2A7BA]">{form.address}</span> and email your evaluation to {form.email}.
                </p>
                <Link
                  to="/"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 hover:bg-[#F2A7BA] transition-colors"
                >
                  Back to home <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="eval-form">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7] mb-2">
                  Exclusive FREE online evaluation
                </p>
                <h2 className="font-display font-light text-2xl sm:text-3xl leading-tight mb-6">
                  Tell me about your home
                </h2>
                <div className="space-y-4">
                  <input
                    data-testid="eval-address"
                    value={form.address}
                    onChange={set("address")}
                    required
                    placeholder="Property address or postal code"
                    className={fieldCls}
                  />
                  <div className="grid sm:grid-cols-3 gap-4">
                    <select data-testid="eval-property-type" value={form.property_type} onChange={set("property_type")} className={`${fieldCls} appearance-none`}>
                      <option value="" className="bg-[#151218]">Property type</option>
                      {PROPERTY_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-[#151218]">{t}</option>
                      ))}
                    </select>
                    <select data-testid="eval-bedrooms" value={form.bedrooms} onChange={set("bedrooms")} className={`${fieldCls} appearance-none`}>
                      <option value="" className="bg-[#151218]">Bedrooms</option>
                      {COUNTS.map((c) => (
                        <option key={c} value={c} className="bg-[#151218]">{c} bed</option>
                      ))}
                    </select>
                    <select data-testid="eval-bathrooms" value={form.bathrooms} onChange={set("bathrooms")} className={`${fieldCls} appearance-none`}>
                      <option value="" className="bg-[#151218]">Bathrooms</option>
                      {COUNTS.map((c) => (
                        <option key={c} value={c} className="bg-[#151218]">{c} bath</option>
                      ))}
                    </select>
                  </div>
                  <select data-testid="eval-timeframe" value={form.timeframe} onChange={set("timeframe")} className={`${fieldCls} appearance-none`}>
                    <option value="" className="bg-[#151218]">When are you thinking of selling?</option>
                    {TIMEFRAMES.map((t) => (
                      <option key={t} value={t} className="bg-[#151218]">{t}</option>
                    ))}
                  </select>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="eval-name" value={form.name} onChange={set("name")} required placeholder="Your name" className={fieldCls} />
                    <input data-testid="eval-phone" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className={fieldCls} />
                  </div>
                  <input data-testid="eval-email" type="email" value={form.email} onChange={set("email")} required placeholder="Your email" className={fieldCls} />
                </div>
                {status === "error" && (
                  <p data-testid="eval-error" className="mt-4 text-sm text-red-400">
                    Couldn&#39;t send your request just now — please try again.
                  </p>
                )}
                <button
                  data-testid="eval-submit"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-6 py-3.5 hover:bg-[#F2A7BA] transition-colors disabled:opacity-60"
                >
                  {status === "sending" && <Loader2 size={16} className="animate-spin" />}
                  {status === "sending" ? "Sending…" : "Get my free evaluation"}
                </button>
                <p className="mt-4 text-xs text-slate-500 font-light text-center">
                  Your details stay private. No obligation to list.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`eval-perk-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-3xl border border-[#E890A7]/15 bg-[#151218]/60 p-7 hover:border-[#E890A7]/40 transition-colors duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#E890A7]/12 border border-[#E890A7]/25 mb-5">
                  <Icon size={22} className="text-[#E890A7]" />
                </span>
                <h3 className="font-display text-xl">{p.title}</h3>
                <p className="mt-2 text-slate-400 font-light text-sm leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500">
          <MapPin size={14} className="text-[#E890A7]" /> Lagoon City, Brechin · Township of Ramara, Ontario
        </p>
      </section>

      <Footer />
    </div>
  );
}
